import { Box } from '@mui/material';
import type { ContractInput } from '@timelog/pm-shared';
import { useTranslation } from 'react-i18next';

import { ContractChip } from './ContractChip';
import { ContractOwnFactor } from './ContractStaffingCharts';

import type { BudgetMetric } from '@/components/financials/metricReading';

/**
 * A contract's heading in Contract insights: its name, then the type badge, the
 * billing method and the set factor on the same line (3 Nov, Mette's notes).
 * Shared by the project view's rows and a parent task's blocks, so a contract
 * reads the same in both.
 */
export const ContractInsightHeading = ({
  contract,
  metric,
  ownFactor,
}: {
  contract: Pick<ContractInput, 'name' | 'type'>;
  metric: BudgetMetric;
  ownFactor: number;
}) => {
  const { t } = useTranslation('pmCommon');
  return (
    <Box
      data-automation-id="contract-insight-heading"
      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
    >
      <Box
        data-automation-id="contract-insight-name"
        sx={{
          minWidth: 0,
          fontSize: 14,
          fontWeight: 500,
          color: 'var(--fg-1)',
          lineHeight: 1.25,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {contract.name}
      </Box>
      <ContractChip type={contract.type} size={17} />
      <Box
        component="span"
        data-automation-id="contract-insight-billing"
        sx={{ flex: 'none', fontSize: 11, color: 'var(--fg-3)' }}
      >
        {t(`billingMethod.${contract.type}`)}
      </Box>
      <ContractOwnFactor metric={metric} ownFactor={ownFactor} />
    </Box>
  );
};
